import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInCalendarDays,
  differenceInDays,
} from 'date-fns';
import { ptBR, enUS, es, fr, de, it } from 'date-fns/locale';

// Os idiomas que o produto de fato usa. Sem correspondencia, `undefined` faz
// o date-fns cair no ingles dele — o comportamento de antes, e nao um erro.
const LOCALES_DE_DATA = {
  'pt-BR': ptBR,
  pt_BR: ptBR,
  pt: ptBR,
  en: enUS,
  'en-US': enUS,
  es,
  fr,
  de,
  it,
};

/**
 * Locale do date-fns a partir do idioma em uso.
 *
 * Sem isto, `formatDistanceToNow` devolve SEMPRE inglês: a tela de
 * Notificações mostrava "3 days ago" e "about 1 month ago" num produto em
 * português. Mesmo bug que o Card-4.6 (2026-04-29) já tinha corrigido em
 * `usePipelineDates.js` — "Today"/"In 12 days" em vez de "Hoje"/"Em 12 dias".
 * A lógica é a mesma e está comentada lá; aqui fica a versão mínima porque
 * `shared/` não pode importar de `dashboard/`.
 *
 * NÃO cravar `ptBR`: este é um fork do Chatwoot e o app suporta outros
 * idiomas. Sem correspondência, o date-fns cai no inglês dele, que é o
 * comportamento de antes.
 */
export const localeDoApp = () => {
  const idioma =
    // eslint-disable-next-line no-underscore-dangle
    window.__APP_I18N__?.global?.locale?.value ||
    window.chatwootConfig?.selectedLocale ||
    document.documentElement.lang ||
    '';
  return LOCALES_DE_DATA[idioma];
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='HH:mm'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
// Relogio de 24h e o padrao daqui: "16:16", nao "4:16 PM". Pedido do Victor
// em 06/09, e coerente com o resto do produto — a Agenda, o Financeiro e a
// ficha do processo ja escreviam a hora assim.
export const messageStamp = (time, dateFormat = 'HH:mm') => {
  const unixTime = fromUnixTime(time);
  if (Number.isNaN(unixTime.getTime())) return '';
  // Locale pelo mesmo motivo do `messageTimestamp`: sem ele o nome do mes sai
  // em ingles ("Feb") num produto em portugues.
  return format(unixTime, dateFormat, { locale: localeDoApp() });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  // `time` às vezes chega undefined/NaN/em formato diferente (ex: mensagem
  // recém-criada via broadcast antes do id local resolver) — sem essa trava,
  // date-fns `format()` joga RangeError: Invalid time value, e como isso
  // roda dentro de uma computed do Vue, derruba a árvore de render inteira
  // (tela de mensagens fica em branco) em vez de só essa mensagem.
  if (Number.isNaN(messageTime.getTime())) return '';
  const now = new Date();
  // Mesma razão do `dynamicTime`: sem locale o date-fns escreve "Sep 3,
  // 4:07 PM" num produto em português. Medido no evento de sistema da
  // conversa em 06/09.
  const opcoes = { locale: localeDoApp() };
  const messageDate = format(messageTime, dateFormat, opcoes);
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, "d 'de' LLL y, HH:mm", opcoes);
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  // Mesma trava de messageTimestamp: achado como causa real da tela em
  // branco pós-edição — TimeAgo.vue (lista lateral) chama isso com
  // last_activity_timestamp que fica momentaneamente undefined durante
  // certos re-renders, e sem guard o RangeError derrubava a árvore inteira.
  if (Number.isNaN(unixTime.getTime())) return '';
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: localeDoApp(),
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  if (Number.isNaN(unixTime.getTime())) return '';
  return format(unixTime, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Digisac-style timestamp for conversation list items.
 * - Same day → "HH:mm" (e.g. "22:33")
 * - Yesterday → "Ontem"
 * - Older → "DD/MM/AAAA" (e.g. "23/06/2026")
 * - Under 1 hour → keep existing short relative format ("5m", "3h")
 * @param {number} unixTimestamp - Unix timestamp in seconds.
 * @returns {string}
 */
const DIAS_DA_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export const conversationListTimestamp = unixTimestamp => {
  if (!unixTimestamp) return '';
  const date = fromUnixTime(unixTimestamp);
  const now = new Date();
  const diffDays = differenceInCalendarDays(now, date);

  if (diffDays === 0) {
    // Today: show time only
    return format(date, 'HH:mm');
  }
  if (diffDays === 1) {
    return 'Ontem';
  }
  // A data inteira ("08/09/2026") tomava o lugar do nome na coluna. Na
  // última semana o dia da semana basta; no ano corrente, dia e mês; o ano
  // só aparece quando muda (16/09/2026).
  if (diffDays > 1 && diffDays < 7) {
    return DIAS_DA_SEMANA[date.getDay()];
  }
  if (isSameYear(date, now)) {
    return format(date, 'dd/MM');
  }
  return format(date, 'dd/MM/yy');
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
